import React, { useRef, useState } from 'react';
import { StyledSelect } from '@routes/sidebar/admin-center/___shared___/components/tables/regular/styled.ts';
import { POPUP_CLASS_NAME } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/constants.ts';
import { RanksCustomRender } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks/ui/RanksCustomRender.tsx';
import { getNormalizedItems } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks/utils.ts';
import type { CustomSelectProps } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';
import { normalizeSelectedSubItems } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/utils.ts';
import { Form } from 'antd';

export const MatricesRanksSelect: React.FC<CustomSelectProps> = ({
    name,
    rules,
    placeholder,
    disabled,
    loading,
    onChange,
    onSearch,
    levelOneLabel,
    levelTwoLabel,
    levelOneItems,
    levelTwoItems,
    subItems,
    searchText,
    selectProps,
    allowClear = true,
    showSearch = true,
    deselectOnly = false,
}) => {
    const form = Form.useFormInstance();
    const selectedSubItems = normalizeSelectedSubItems(Form.useWatch(name, form));
    const initialSelectedSubItemsRef = useRef<string[] | null>(null);

    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const labelByValue = new Map(subItems.map((si) => [String(si.value), si.label]));

    const selectedOptions = selectedSubItems.map((value) => {
        const v = String(value);
        return { value: v, label: labelByValue.get(v) ?? v };
    });

    const normalizedLevelOneItems = getNormalizedItems(levelOneItems);
    const normalizedLevelTwoItems = getNormalizedItems(levelTwoItems, false);

    return (
        <Form.Item name={name} rules={rules} style={{ margin: 0 }}>
            <StyledSelect
                {...selectProps}
                classNames={{
                    popup: {
                        root: POPUP_CLASS_NAME,
                    },
                }}
                mode="multiple"
                open={open}
                onOpenChange={(nextOpen) => {
                    if (nextOpen && initialSelectedSubItemsRef.current === null) {
                        initialSelectedSubItemsRef.current = [...selectedSubItems];
                    }

                    if (!nextOpen) {
                        initialSelectedSubItemsRef.current = null;
                    }

                    setOpen(nextOpen);
                }}
                popupRender={() => (
                    <div onMouseDown={(e) => e.preventDefault()}>
                        <RanksCustomRender
                            levelOneLabel={levelOneLabel}
                            levelTwoLabel={levelTwoLabel}
                            levelOneItems={normalizedLevelOneItems}
                            levelTwoItems={normalizedLevelTwoItems}
                            subItems={subItems}
                            searchText={searchText}
                            selectedSubItems={selectedSubItems}
                            deselectOnly={deselectOnly}
                            initiallySelectedSubItems={initialSelectedSubItemsRef.current ?? selectedSubItems}
                            onCancel={handleClose}
                            onDone={(values) => {
                                form.setFieldValue(name, values);
                                onChange?.(values);
                                handleClose();
                            }}
                        />
                    </div>
                )}
                value={selectedSubItems}
                onChange={(values) => {
                    form.setFieldValue(name, values);
                    onChange?.(values as string[]);
                }}
                placeholder={placeholder}
                disabled={disabled}
                loading={loading}
                allowClear={allowClear}
                showSearch={
                    showSearch
                        ? {
                              onSearch,
                          }
                        : false
                }
                options={selectedOptions}
            />
        </Form.Item>
    );
};
